import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trash2, Mail, ShieldCheck, Clock, FileX } from "lucide-react";

const CONTACT_EMAIL = "privacy@geonetix.live";

const MAIL_SUBJECT = "Account & Data Deletion Request";
const MAIL_BODY = [
  "Hello GeoNetix Team,",
  "",
  "I would like to request the permanent deletion of my GeoNetix account and all associated personal data.",
  "",
  "Registered email / username: ____________________",
  "Device ID (if known): ____________________",
  "",
  "I understand this action is irreversible.",
  "",
  "Thank you.",
].join("\n");

const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  MAIL_SUBJECT
)}&body=${encodeURIComponent(MAIL_BODY)}`;

const WHAT_GETS_DELETED = [
  ["Account profile", "Your email, username, and profile metadata."],
  ["Device & network telemetry", "Device identifiers, IP records, and network telemetry linked to your account."],
  ["Earnings & payout records", "Balance history and payout data, except records we must retain for legal, tax, or fraud-prevention obligations."],
  ["Referral connections", "Your referral links and associated account connections."],
];

const STEPS = [
  {
    icon: Mail,
    title: "Submit your request",
    desc: "Click the button below to email us from your registered address. Include your username/email and Device ID so we can verify ownership.",
  },
  {
    icon: ShieldCheck,
    title: "Identity verification",
    desc: "We verify the request is genuinely from the account owner to protect your data from unauthorized deletion.",
  },
  {
    icon: Clock,
    title: "Processing window",
    desc: "Verified requests are processed within 30 days. Some records may be retained where required by law (e.g., tax/payout reporting).",
  },
  {
    icon: FileX,
    title: "Confirmation",
    desc: "You'll receive an email confirming that your account and eligible data have been permanently removed.",
  },
];

export default function DataDeletion() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="data-deletion-page"
      className="min-h-screen bg-[#0A192F] text-slate-100 antialiased selection:bg-[#4CAF50] selection:text-[#04140a]"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="deletion-logo-home" className="flex items-center">
            <img
              src="/geonetix-logo-full.webp"
              alt="GeoNetix"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="deletion-back-home"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 gx-grid-bg opacity-40" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#00B4D8]/10 blur-[120px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4"
          >
            / Account Control
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            Delete Your <span className="gx-text-gradient">Account & Data</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-2xl text-slate-300 leading-relaxed"
          >
            You are in full control of your data. Use the button below to request
            permanent deletion of your GeoNetix account and its associated personal
            information. This action is irreversible once completed.
          </motion.p>

          <motion.a
            href={MAILTO}
            data-testid="request-deletion-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-[#4CAF50] px-7 py-4 text-[15px] font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow"
          >
            <Trash2 size={18} className="transition-transform duration-200 group-hover:-translate-y-0.5" />
            Request Account Deletion
          </motion.a>
          <p className="mt-4 text-sm text-slate-400">
            Or email us directly at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="deletion-email-link"
              className="text-[#00B4D8] hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-20 space-y-16">
        {/* What gets deleted */}
        <section data-testid="deletion-what-section">
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-6">
            What gets deleted
          </h2>
          <ul className="space-y-3">
            {WHAT_GETS_DELETED.map(([term, desc], i) => (
              <li
                key={i}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
              >
                <span className="font-semibold text-white">{term}: </span>
                <span className="text-slate-400">{desc}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* How it works */}
        <section data-testid="deletion-steps-section">
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-8">
            How the request works
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00B4D8]/10 border border-[#00B4D8]/30">
                      <Icon size={20} className="text-[#00B4D8]" />
                    </div>
                    <span className="font-mono-gx text-sm text-[#4CAF50]">
                      Step {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-semibold text-white text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Note */}
        <div className="rounded-3xl border border-[#00B4D8]/25 bg-gradient-to-r from-[#00B4D8]/10 to-transparent p-8">
          <p className="text-slate-300 leading-relaxed">
            <span className="font-semibold text-white">Please note: </span>
            Certain records may be retained after deletion where we are legally
            required to keep them (for example, tax, payout, and fraud-prevention
            obligations). All other personal data tied to your profile will be
            permanently erased. For more detail, see our{" "}
            <Link to="/privacy-policy" className="text-[#00B4D8] hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div className="text-center">
          <Link
            to="/"
            data-testid="deletion-cta-home"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Return to GeoNetix
          </Link>
        </div>
      </main>

      {/* Bottom bar */}
      <footer className="border-t border-white/10 bg-[#050d1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-9 w-auto object-contain" />
          <div className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} GeoNetix. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
